import { BaseScene } from './BaseScene';
import { SceneKey } from './sceneKeys';
import { GAME_WIDTH, GAME_HEIGHT } from './gameConfig';
import {
  BATTLE_MODE_SELECT_BUTTONS,
  BATTLE_MODE_SELECT_LABELS,
} from './battleModeSelectConfig';
import { BackgroundImageKey } from '../constants/imageKeys';
import { AudioKey } from '../constants/audioKeys';
import { playSe } from '../utils/audioManager';
import { GameMode } from '../types/GameMode';
import { loadSurvivalSave, loadSurvivalBest } from '../utils/survivalManager';

/**
 * 対戦モード選択画面シーン
 *
 * 対CPU / フレンド対戦 / 観戦 / 戻る のボタンを表示する。
 */
export class BattleModeSelectScene extends BaseScene {
  constructor() {
    super(SceneKey.BATTLE_MODE_SELECT);
  }

  create(): void {
    this.cameras.main.fadeIn(300, 0, 0, 0);

    if (this.textures.exists(BackgroundImageKey.TITLE)) {
      this.add.image(GAME_WIDTH / 2, GAME_HEIGHT / 2, BackgroundImageKey.TITLE).setOrigin(0.5);
    }

    this.createTitle();
    this.createButtons();
  }

  private createTitle(): void {
    this.add
      .text(GAME_WIDTH / 2, GAME_HEIGHT * 0.2, BATTLE_MODE_SELECT_LABELS.title, {
        fontSize: '40px',
        color: '#ffffff',
        fontFamily: "'Noto Sans JP', Arial, sans-serif",
        fontStyle: 'bold',
      })
      .setOrigin(0.5);
  }

  private createButtons(): void {
    const startY = GAME_HEIGHT * 0.4;
    const spacing = 60;

    BATTLE_MODE_SELECT_BUTTONS.forEach((buttonConfig, index) => {
      const y = startY + index * spacing;

      const text = this.add
        .text(GAME_WIDTH / 2, y, buttonConfig.label, {
          fontSize: '28px',
          color: '#ffffff',
          fontFamily: "'Noto Sans JP', Arial, sans-serif",
          stroke: '#000000',
          strokeThickness: 5,
        })
        .setOrigin(0.5)
        .setInteractive({ useHandCursor: true });

      text.on('pointerover', () => {
        text.setColor('#ffffff');
        text.setScale(1.1);
      });

      text.on('pointerout', () => {
        text.setColor('#ffffff');
        text.setScale(1.0);
      });

      text.on('pointerdown', () => {
        playSe(this.sound, AudioKey.SE_SELECT);
        if (buttonConfig.mode === GameMode.SURVIVAL) {
          this.handleSurvivalButton();
        } else {
          this.transitionTo(buttonConfig.targetScene, {
            mode: buttonConfig.mode,
          });
        }
      });
    });
  }

  private handleSurvivalButton(): void {
    const save = loadSurvivalSave();
    if (!save) {
      this.transitionTo(SceneKey.CHARACTER_SELECT, { mode: GameMode.SURVIVAL });
      return;
    }

    // セーブデータがある場合はダイアログで選択
    const cx = GAME_WIDTH / 2;
    const cy = GAME_HEIGHT / 2;
    const overlay = this.add.rectangle(cx, cy, GAME_WIDTH, GAME_HEIGHT, 0x000000, 0.7);
    const panel = this.add.rectangle(cx, cy, 420, 200, 0x222222).setStrokeStyle(2, 0x888888);

    const best = loadSurvivalBest(save.monsterId);
    const bestText = best > 0 ? ` / 最高: ${best}` : '';
    this.add
      .text(cx, cy - 60, `続きから再開しますか？\n（${save.winCount}連勝中${bestText}）`, {
        fontSize: '18px',
        color: '#ffffff',
        wordWrap: { width: 380 },
        align: 'center',
      })
      .setOrigin(0.5);

    const resumeBtn = this.add
      .text(cx - 90, cy + 30, '続きから', {
        fontSize: '20px',
        color: '#ffffff',
        backgroundColor: '#224422',
        padding: { x: 16, y: 8 },
      })
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true });

    resumeBtn.on('pointerdown', () => {
      playSe(this.sound, AudioKey.SE_SELECT);
      this.transitionTo(SceneKey.SURVIVAL_EXP_ALLOCATION, { saveData: save });
    });

    const newBtn = this.add
      .text(cx + 90, cy + 30, '新しく始める', {
        fontSize: '20px',
        color: '#ffffff',
        backgroundColor: '#442222',
        padding: { x: 16, y: 8 },
      })
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true });

    newBtn.on('pointerdown', () => {
      playSe(this.sound, AudioKey.SE_SELECT);
      overlay.destroy();
      panel.destroy();
      this.transitionTo(SceneKey.CHARACTER_SELECT, { mode: GameMode.SURVIVAL });
    });
  }
}
